import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Chip, Button } from '@mui/material';
import { motion } from 'framer-motion';
import EuroIcon from '@mui/icons-material/Euro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPrices } from '../../services/storage';
import { PRICE_CATEGORIES } from './PreisePage';
import { PREISE_KEY, PREISE_DEFAULTS, applyCategoryContent } from '../../content/preise';
import { useSiteContent } from '../../hooks/useSiteContent';
import SEOHead from '../../components/SEOHead';
import { metaDescription } from '../../utils/seo';
import type { PriceCategory, PriceSection } from './PreisePage';

const MotionBox = motion(Box);

const PriceCategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const location = useLocation();
  const { t } = useLanguage();
  const [pricesMap, setPricesMap] = useState<Record<string, string>>({});

  const preiseContent = useSiteContent(PREISE_KEY, PREISE_DEFAULTS);
  const rawCategory: PriceCategory | undefined =
    PRICE_CATEGORIES.find((c) => c.id === category) ??
    (location.state as { category?: PriceCategory })?.category;
  const baseCategory = rawCategory && applyCategoryContent(rawCategory, preiseContent);

  useEffect(() => {
    window.scrollTo(0, 0);
    getPrices().then(setPricesMap).catch(() => {});
  }, [category]);

  if (!baseCategory) {
    return (
      <Container maxWidth="lg" sx={{ py: 16, textAlign: 'center' }}>
        <SEOHead title="Kategorie nicht gefunden | TOP BHS" noindex />
        <Typography variant="h4">Kategorie nicht gefunden.</Typography>
        <Button component={RouterLink} to="/preise" sx={{ mt: 3 }} startIcon={<ArrowBackIcon />}>
          Zurück zu Preise
        </Button>
      </Container>
    );
  }

  const mergedSections: PriceSection[] = baseCategory.sections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      price: item.price && pricesMap[item.labelKey] ? pricesMap[item.labelKey] : item.price,
    })),
  }));

  return (
    <Box>
      <SEOHead
        title={t('seo.preise_category_title', { category: t(baseCategory.titleKey) })}
        description={metaDescription(t('seo.preise_category_description', { category: t(baseCategory.titleKey) }))}
      />
      {/* ── Hero ── */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 300, md: 400 },
          overflow: 'hidden',
          backgroundImage: `url(${baseCategory.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.78) 100%)' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pb: { xs: 5, md: 7 } }}>
          <MotionBox initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Chip
              icon={<EuroIcon sx={{ fontSize: '13px !important', color: '#7b9ef0 !important' }} />}
              label={t('preise.lohn_chip')}
              sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.1em', mb: 2.5, border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(6px)' }}
            />
            <Typography variant="h1" sx={{ color: 'white', fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-1px', lineHeight: 1.1, mb: 1.5 }}>
              {t(baseCategory.titleKey)}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '1rem', maxWidth: 480, lineHeight: 1.6 }}>
              {t(baseCategory.subtitleKey)}
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      {/* ── Back link ── */}
      <Box sx={{ bgcolor: '#f8f8f8', borderBottom: '1px solid #ebebeb' }}>
        <Container maxWidth="lg" sx={{ py: 1.5 }}>
          <Button
            component={RouterLink}
            to="/preise"
            startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
            sx={{ color: 'text.secondary', fontSize: '0.82rem', textTransform: 'none', '&:hover': { color: '#1737bc', bgcolor: 'transparent' } }}
          >
            Zurück zu Preise
          </Button>
        </Container>
      </Box>

      {/* ── Price Table ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>

        {mergedSections.map((section, sIdx) => (
          <MotionBox
            key={section.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: sIdx * 0.06 }}
            viewport={{ once: true }}
            sx={{ mb: 5, border: '1px solid #e0e0e8', borderRadius: 2, overflow: 'hidden' }}
          >
            {/* Section Header */}
            <Box sx={{ bgcolor: '#1a2e5a', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                {t(section.titleKey)}
              </Typography>
              {section.subtitleKey && (
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>
                  {t(section.subtitleKey)}
                </Typography>
              )}
            </Box>

            {/* Column Headers */}
            <Box sx={{ display: 'flex', bgcolor: '#eef1f8', borderBottom: '1px solid #d8dce8', px: 3, py: 1.25 }}>
              <Typography sx={{ width: 52, flexShrink: 0, fontWeight: 700, fontSize: '0.78rem', color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Nr.
              </Typography>
              <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '0.78rem', color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Leistung
              </Typography>
              {baseCategory.priceHeader && (
                <Typography sx={{ width: { xs: 130, md: 200 }, flexShrink: 0, fontWeight: 700, fontSize: '0.78rem', color: '#1737bc', textAlign: 'right', letterSpacing: '0.3px' }}>
                  {baseCategory.priceHeader}
                </Typography>
              )}
              {!baseCategory.priceHeader && (
                <Typography sx={{ width: { xs: 100, md: 150 }, flexShrink: 0, fontWeight: 700, fontSize: '0.78rem', color: '#444', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Preis
                </Typography>
              )}
            </Box>

            {/* Items */}
            {section.items.map((item, i) => {
              const isHeader = item.isGroupHeader;
              const isSub = item.isSubItem;
              // Calculate simple sequential numbers
              let displayNum = '';
              if (!isSub && !isHeader) {
                displayNum = String(section.items.filter((x, xi) => xi <= i && !x.isSubItem && !x.isGroupHeader).length);
              } else if (isHeader) {
                displayNum = String(section.items.filter((x, xi) => xi <= i && !x.isSubItem).length + section.items.filter((x, xi) => xi < i && x.isGroupHeader).length);
                // Count non-sub items up to this point
                let cnt = 0;
                for (let k = 0; k <= i; k++) {
                  if (!section.items[k].isSubItem) cnt++;
                }
                displayNum = String(cnt);
              } else {
                // sub-item: find parent group header index and sub-item index after it
                let parentNum = 0;
                let subIdx = 0;
                let foundParent = false;
                let nonSubCount = 0;
                for (let k = 0; k < section.items.length; k++) {
                  if (!section.items[k].isSubItem) nonSubCount++;
                  if (k === i) break;
                  if (section.items[k].isGroupHeader) {
                    parentNum = nonSubCount;
                    subIdx = 0;
                    foundParent = true;
                  } else if (section.items[k].isSubItem && foundParent) {
                    subIdx++;
                  }
                }
                displayNum = `${parentNum}.${subIdx + 1}`;
              }

              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    px: 3,
                    py: isHeader ? 1.5 : 1.25,
                    borderBottom: i < section.items.length - 1 ? '1px solid #f0f0f5' : 'none',
                    bgcolor: isHeader ? '#f4f6fe' : isSub ? '#fafafa' : i % 2 === 0 ? '#ffffff' : '#fafbff',
                    '&:hover': { bgcolor: isHeader ? '#eef0fc' : '#f5f7ff' },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {/* Nr. */}
                  <Typography sx={{
                    width: 52, flexShrink: 0, fontSize: '0.85rem',
                    fontWeight: isHeader ? 700 : 400,
                    color: isHeader ? '#1737bc' : isSub ? '#888' : '#555',
                    pt: '1px',
                  }}>
                    {displayNum}
                  </Typography>

                  {/* Label */}
                  <Typography sx={{
                    flex: 1,
                    fontSize: isSub ? '0.82rem' : '0.9rem',
                    fontWeight: isHeader ? 700 : 400,
                    color: isHeader ? '#1737bc' : isSub ? '#666' : '#1a2640',
                    lineHeight: 1.5,
                    pl: isSub ? 1.5 : 0,
                  }}>
                    {t(item.labelKey)}
                  </Typography>

                  {/* Price */}
                  <Typography sx={{
                    width: { xs: 130, md: 200 }, flexShrink: 0,
                    textAlign: 'right',
                    fontSize: isHeader ? '0.9rem' : '0.88rem',
                    fontWeight: isHeader ? 800 : item.highlight ? 700 : 600,
                    color: isHeader ? '#c0392b' : item.highlight ? '#1737bc' : '#1a2640',
                    whiteSpace: 'nowrap',
                    pt: '1px',
                  }}>
                    {item.price || ''}
                  </Typography>
                </Box>
              );
            })}
          </MotionBox>
        ))}

        {/* Legal Note */}
        {baseCategory.legalNote && (
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            sx={{ border: '1px solid #d0d5f8', borderRadius: 2, overflow: 'hidden', mb: 6 }}
          >
            <Box sx={{ bgcolor: '#1a2e5a', px: 3, py: 1.5 }}>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.85rem' }}>
                Allgemeine Hinweise
              </Typography>
            </Box>
            <Box sx={{ bgcolor: '#f8f9fe', px: 3, py: 2.5 }}>
              {baseCategory.legalNote.map((line, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontSize: '0.82rem',
                    color: line.startsWith('*') ? '#555' : '#444',
                    lineHeight: 1.75,
                    mb: i < baseCategory.legalNote!.length - 1 ? 0.75 : 0,
                    fontWeight: line.startsWith('Wir erbringen') ? 600 : 400,
                  }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          </MotionBox>
        )}

        {/* CTA */}
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          sx={{ p: { xs: 3, md: 5 }, bgcolor: '#f4f6fe', borderRadius: 3, border: '1px solid #d0d5f8', textAlign: 'center' }}
        >
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1.5, color: '#1a2640' }}>
            {t('preise.lohn_cta_title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 420, mx: 'auto', lineHeight: 1.7 }}>
            {t('preise.lohn_cta_text')}
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            size="large"
            startIcon={<CalendarMonthIcon />}
            sx={{ bgcolor: '#1737bc', color: 'white', px: 4, py: 1.5, '&:hover': { bgcolor: '#0f2899' } }}
          >
            {t('preise.lohn_cta_btn')}
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default PriceCategoryPage;
